import { useCallback, useEffect, useRef, MutableRefObject, Dispatch } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChatSession,
  Customer,
  Message,
  MessageRole,
  MessageWithObservability,
  ObservabilityEvent,
} from '../../../types';
import { WebSocketService } from '../services/websocketService';
import { useAuth } from '../../auth/context/AuthContext';
import { createInvestigationService } from '../../investigation/services/investigationService';
import { ChatAction, ChatState } from './useChatReducer';
import { chatMessageKeys } from './chatMessageMapping';

const QUEUE_STORAGE_KEY = (investigationId: string) => `chat_queue_${investigationId}`;

function loadQueue(investigationId: string): string[] {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY(investigationId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(investigationId: string, queue: string[]): void {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(QUEUE_STORAGE_KEY(investigationId));
    } else {
      localStorage.setItem(QUEUE_STORAGE_KEY(investigationId), JSON.stringify(queue));
    }
  } catch {
    // localStorage unavailable — queue lives only in memory
  }
}

interface UseChatStreamingParams {
  state: ChatState;
  dispatch: Dispatch<ChatAction>;
  wsRef: MutableRefObject<WebSocketService | null>;
  activeSessionIdRef: MutableRefObject<string | null>;
  streamingMessageRef: MutableRefObject<string>;
  streamingObservabilityRef: MutableRefObject<ObservabilityEvent[]>;
  createNewSession: (customer?: Customer) => Promise<ChatSession | null>;
  customerId?: string;
}

export function useChatStreaming({
  state,
  dispatch,
  wsRef,
  activeSessionIdRef,
  streamingMessageRef,
  streamingObservabilityRef,
  createNewSession,
  customerId,
}: UseChatStreamingParams) {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  // Stable ref so STREAMING_COMPLETE effect can call sendMessage without stale closure
  const sendMessageRef = useRef<((content: string) => Promise<void>) | null>(null);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;
    if (!user) {
      dispatch({ type: 'ERROR', error: 'Not authenticated' });
      return;
    }

    // Queue if streaming is in progress
    if (state.status === 'loading' || state.status === 'streaming') {
      dispatch({ type: 'MESSAGE_QUEUED', content });
      const investigationId = state.currentSession?.id;
      if (investigationId) {
        const updatedQueue = [...state.messageQueue, content];
        saveQueue(investigationId, updatedQueue);
      }
      return;
    }

    streamingMessageRef.current = '';
    streamingObservabilityRef.current = [];

    try {
      let session = state.currentSession;

      if (!session) {
        if (!customerId) {
          dispatch({ type: 'ERROR', error: 'No customer selected' });
          return;
        }
        session = await createNewSession({ id: customerId } as Customer);
        if (!session) {
          return;
        }
      }

      flushSync(() => {
        dispatch({ type: 'SENDING_MESSAGE', content });
      });

      const cachedMessages = queryClient.getQueryData<MessageWithObservability[]>(
        chatMessageKeys.list(customerId, session.id)
      );
      if ((cachedMessages?.length || 0) === 0) {
        const updatedSession = {
          ...session,
          title: content.slice(0, 50) + (content.length > 50 ? '...' : ''),
        };
        dispatch({ type: 'SESSION_UPDATED', session: updatedSession });
      }

      if (!wsRef.current) {
        throw new Error('WebSocket service not initialized');
      }

      if (!wsRef.current.isConnected()) {
        if (!user.sub) {
          throw new Error('User OID not available - required for chat connection');
        }
        // The server authorizes the connection against this customer, so an
        // absent one is a bug rather than something to connect without.
        if (!customerId) {
          throw new Error('No customer selected - required for chat connection');
        }
        await wsRef.current.connect(session.id, user.sub, customerId, idToken || undefined);
      }

      await wsRef.current.sendMessage('sendMessage', { message: content });

      const userMessage: Message = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        content,
        role: MessageRole.USER,
        timestamp: new Date().toISOString(),
        investigation_id: session.id,
      };

      // Cancel any in-flight history read for this session before the optimistic
      // append: a freshly-created session fires an empty-history fetch that can
      // resolve after this append and overwrite the user message we're adding.
      await queryClient.cancelQueries({ queryKey: chatMessageKeys.list(customerId, session.id) });

      // Append to the query cache (ADR-0049) — never invalidate, which would drop
      // the streamed turns not yet persisted server-side.
      queryClient.setQueryData<MessageWithObservability[]>(
        chatMessageKeys.list(customerId, session.id),
        (old = []) => [...old, userMessage]
      );

      flushSync(() => {
        dispatch({ type: 'MESSAGE_SENT' });
      });

    } catch (err) {
      dispatch({
        type: 'ERROR',
        error: err instanceof Error ? err.message : 'Failed to send message'
      });
    }
  }, [state.status, state.currentSession, state.messageQueue, user, createNewSession, customerId, idToken, wsRef, streamingMessageRef, streamingObservabilityRef, dispatch, queryClient]);

  // Keep sendMessageRef stable so the dequeue effect can call it
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // Restore queue from localStorage when session changes
  useEffect(() => {
    const investigationId = state.currentSession?.id;
    if (!investigationId) return;
    const persisted = loadQueue(investigationId);
    if (persisted.length > 0) {
      for (const msg of persisted) {
        dispatch({ type: 'MESSAGE_QUEUED', content: msg });
      }
      saveQueue(investigationId, []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentSession?.id]);

  // After streaming completes, send next queued message
  useEffect(() => {
    if (state.status !== 'idle') return;
    if (state.messageQueue.length === 0) return;
    const next = state.messageQueue[0];
    dispatch({ type: 'DEQUEUE_MESSAGE' });
    const investigationId = state.currentSession?.id;
    if (investigationId) {
      const remaining = state.messageQueue.slice(1);
      saveQueue(investigationId, remaining);
    }
    sendMessageRef.current?.(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.messageQueue]);

  const cancelStream = useCallback(async () => {
    streamingMessageRef.current = '';
    dispatch({ type: 'CLEAR_STREAMING' });

    const investigationId = activeSessionIdRef.current;
    if (investigationId) {
      saveQueue(investigationId, []);
    }

    if (wsRef.current && investigationId) {
      try {
        await wsRef.current.sendMessage('cancelStream', {
          investigationId
        });
      } catch (error) {
        console.error('Failed to send cancel to backend:', error);
      }
    }
  }, [wsRef, activeSessionIdRef, streamingMessageRef, dispatch]);

  const clearConversation = useCallback(async () => {
    const investigationId = activeSessionIdRef.current;
    if (!investigationId || !customerId || !idToken) return;

    try {
      const investigationService = createInvestigationService(idToken);
      await investigationService.clearConversation(customerId, investigationId);
      queryClient.setQueryData<MessageWithObservability[]>(
        chatMessageKeys.list(customerId, investigationId),
        []
      );
      dispatch({ type: 'CONVERSATION_CLEARED' });
    } catch (error) {
      console.error('Failed to clear conversation:', error);
      dispatch({ type: 'ERROR', error: 'Failed to clear conversation' });
    }
  }, [activeSessionIdRef, customerId, idToken, dispatch, queryClient]);

  const editMessage = useCallback(async (_messageId: string, _newContent: string) => {
    // TODO: Implement message editing
  }, []);

  return {
    sendMessage,
    cancelStream,
    clearConversation,
    editMessage,
  };
}
